import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Settings, Network } from "lucide-react";

const EditTerminalModal = ({
  open,
  onOpenChange,
  editingTerminal,
  editFormData,
  setEditFormData,
  sites,
  terminalTypes,
  capabilities,
  communicationModes,
  handleUpdateTerminal,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t("terminalMgmt.edit.title", { name: editingTerminal?.name })}</DialogTitle>
          <DialogDescription>{t("terminalMgmt.edit.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="border-b pb-2">
              <h3 className="text-lg font-medium text-foreground">{t("terminalMgmt.edit.basicInfo")}</h3>
              <p className="text-sm text-muted-foreground">{t("terminalMgmt.edit.basicInfoDesc")}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-sm font-medium">
                  {t("terminalMgmt.form.nameLabel")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-name"
                  value={editFormData.name || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder={t("terminalMgmt.form.namePlaceholder")}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("terminalMgmt.form.commModeLabel")} <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={editFormData.communication_mode}
                  onValueChange={(value) => {
                    setEditFormData({ ...editFormData, communication_mode: value });
                    if (value === "push") {
                      setEditFormData((prev) => ({ ...prev, ip_address: "" }));
                    }
                  }}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {communicationModes.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-600">{mode.value === "push" ? "📡" : "🔗"}</span>
                          <span>{mode.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editFormData.communication_mode === "push" && (
              <div className="space-y-2">
                <Label htmlFor="edit-serial" className="text-sm font-medium">
                  {t("terminalMgmt.form.serialLabel")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-serial"
                  value={editFormData.serial_number || ""}
                  onChange={(e) => setEditFormData({ ...editFormData, serial_number: e.target.value })}
                  placeholder={t("terminalMgmt.form.serialPlaceholder")}
                  className="h-10"
                />
                <p className="text-xs text-muted-foreground">{t("terminalMgmt.edit.serialAdmsHint")}</p>
              </div>
            )}

            {editFormData.communication_mode === "pull" && (
              <div className="p-4 bg-orange-950/20 rounded-lg border border-orange-900/40 space-y-4">
                <div className="flex items-center space-x-2">
                  <Network className="w-5 h-5 text-orange-400" />
                  <h4 className="font-medium text-foreground">{t("terminalMgmt.edit.networkConfig")}</h4>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="edit-ip" className="text-sm font-medium">{t("terminalMgmt.edit.ipLabel")}</Label>
                    <Input
                      id="edit-ip"
                      value={editFormData.ip_address || ""}
                      onChange={(e) => setEditFormData({ ...editFormData, ip_address: e.target.value })}
                      placeholder="192.168.1.100"
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-port" className="text-sm font-medium">{t("terminalMgmt.edit.portLabel")}</Label>
                    <Input
                      id="edit-port"
                      type="number"
                      value={editFormData.port || ""}
                      onChange={(e) => setEditFormData({ ...editFormData, port: parseInt(e.target.value) || 4370 })}
                      placeholder="4370"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("terminalMgmt.form.typeLabel")}</Label>
              <Select
                value={editFormData.terminal_type}
                onValueChange={(value) => setEditFormData({ ...editFormData, terminal_type: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {terminalTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("terminalMgmt.form.siteLabel")} *</Label>
              <Select
                value={editFormData.site_id ? editFormData.site_id.toString() : ""}
                onValueChange={(value) => setEditFormData({ ...editFormData, site_id: parseInt(value) })}
              >
                <SelectTrigger><SelectValue placeholder={t("terminalMgmt.form.sitePlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      <div className="flex items-center space-x-2">
                        <span>📍</span><span>{site.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("terminalMgmt.edit.capabilitiesLabel")}</Label>
            <div className="grid grid-cols-2 gap-2">
              {capabilities.map((capability) => (
                <div key={capability.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-${capability.value}`}
                    checked={(editFormData.capabilities || []).includes(capability.value)}
                    onCheckedChange={(checked) => {
                      const current = editFormData.capabilities || [];
                      setEditFormData({
                        ...editFormData,
                        capabilities: checked
                          ? [...current, capability.value]
                          : current.filter((c) => c !== capability.value),
                      });
                    }}
                  />
                  <Label htmlFor={`edit-${capability.value}`} className="text-sm">
                    {capability.icon} {capability.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-model">{t("terminalMgmt.form.modelLabel")}</Label>
              <Input
                id="edit-model"
                value={editFormData.model || ""}
                onChange={(e) => setEditFormData({ ...editFormData, model: e.target.value })}
                placeholder={t("terminalMgmt.form.modelPlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-firmware">{t("terminalMgmt.form.firmwareLabel")}</Label>
              <Input
                id="edit-firmware"
                value={editFormData.firmware_version || ""}
                onChange={(e) => setEditFormData({ ...editFormData, firmware_version: e.target.value })}
                placeholder={t("terminalMgmt.form.firmwarePlaceholder")}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="edit-active"
              checked={editFormData.is_active !== false}
              onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_active: checked })}
            />
            <Label htmlFor="edit-active" className="text-sm font-medium">{t("terminalMgmt.edit.activeLabel")}</Label>
          </div>
        </div>

        <div className="flex items-center justify-between pt-6 border-t">
          <div className="text-sm text-muted-foreground">{t("terminalMgmt.form.requiredFields")}</div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="px-6">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleUpdateTerminal}
              className="px-8 bg-blue-600 hover:bg-blue-700"
              disabled={
                !editFormData.name ||
                !editFormData.site_id ||
                (editFormData.communication_mode === "push" && !editFormData.serial_number) ||
                (editFormData.communication_mode === "pull" && !editFormData.ip_address)
              }
            >
              <Settings className="w-4 h-4 mr-2" />
              {t("terminalMgmt.edit.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditTerminalModal;
